import {
  Calendar,
  PenTool,
} from "lucide-react";
import { useEffect, useState } from "react";
import api from "../../services/api";
import DailyReviewForm from "./DailyReviewForm";
import DatePickerHeader from "./DatePickerHeader";
import TransactionContext from "./TransactionContext";
import UnifiedComposer from "./UnifiedComposer";

const JournalInput = ({ onSubmit, disabled }) => {
  const [activeTab, setActiveTab] = useState("log"); // 'log', 'review'
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  
  // Transaction data for selected date
  const [transactions, setTransactions] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [summary, setSummary] = useState({});
  const [transactionDates, setTransactionDates] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // Selected transactions to journal
  const [selectedTransactions, setSelectedTransactions] = useState([]);

  // Fetch transactions when date changes
  useEffect(() => {
    fetchTransactionsForDate(selectedDate);
  }, [selectedDate]);

  // Fetch available transaction dates for calendar highlighting
  useEffect(() => {
    fetchTransactionDates();
  }, []);

  const fetchTransactionsForDate = async (date) => {
    setLoadingTransactions(true);
    try {
      const { data } = await api.get(`/journal-transactions/by-date/${date}`);
      if (data.success) {
        setTransactions(data.data.transactions || []);
        setGrouped(data.data.grouped || []);
        setSummary(data.data.summary || {});
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      setTransactions([]);
      setGrouped([]);
      setSummary({});
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchTransactionDates = async () => {
    try {
      // Get last 3 months of transaction dates
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 3);
      
      const { data } = await api.get("/journal-transactions/dates", {
        params: { startDate: startDate.toISOString().split("T")[0] }
      });
      
      if (data.success) {
        setTransactionDates(data.data.dates || []);
      }
    } catch (error) {
      console.error("Failed to fetch transaction dates:", error);
    }
  };

  const handleSuccess = (data) => {
    // Refresh transactions for current date
    fetchTransactionsForDate(selectedDate);
    
    // Clear selected transactions
    setSelectedTransactions([]);
    
    // If data is passed (like from QuickNote), call parent onSubmit
    if (data) {
      onSubmit(data);
    } else {
      // If no data, trigger refresh signal
      onSubmit({ entryType: "refresh_signal" });
    }
  };

  const handleSelectTransactions = (txns) => {
    setSelectedTransactions(txns);
    setActiveTab("log"); // Switch to log tab
  };

  const handleLogSubmit = (entry) => {
    fetchTransactionsForDate(selectedDate);
    setSelectedTransactions([]);
    onSubmit(entry);
  };

  const isToday = selectedDate === new Date().toISOString().split("T")[0];
  const isFuture = new Date(selectedDate) > new Date();
  const isPast = !isToday && !isFuture;

  return (
    <div className="space-y-4 animate-fade-in">
      
      {/* Date Picker Header */}
      <DatePickerHeader
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        transactionDates={transactionDates}
      />

      {/* Transaction Context - Show for past dates with transactions */}
      {isPast && (transactions.length > 0 || loadingTransactions) && (
        <TransactionContext
          transactions={transactions}
          grouped={grouped}
          summary={summary}
          selectedDate={selectedDate}
          onSelectTransaction={handleSelectTransactions}
          loading={loadingTransactions}
        />
      )}

      {/* Future Date Banner */}
      {isFuture && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
          <Calendar className="mx-auto text-blue-400 mb-2" size={24} />
          <p className="text-blue-400 font-medium">Planning for the Future</p>
          <p className="text-slate-400 text-sm mt-1">
            Add goals, watchlist, or planned trades for this date
          </p>
        </div>
      )}

      {/* Main Input Card */}
      <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700 overflow-hidden">
        
        {/* Tabs Header */}
        <div className="flex border-b border-slate-700 bg-slate-900/50">
          <button
            onClick={() => setActiveTab("log")}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === "log"
                ? "text-indigo-400 border-b-2 border-indigo-500 bg-slate-800"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
            }`}
          >
            <PenTool size={16} />
            Log
            {selectedTransactions.length > 0 && (
              <span className="bg-emerald-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {selectedTransactions.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("review")}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === "review"
                ? "text-amber-400 border-b-2 border-amber-500 bg-slate-800"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
            }`}
          >
            <Calendar size={16} /> Daily Review
          </button>
        </div>

        {/* Forms Area */}
        <div className="p-4">
          {activeTab === "log" && (
            <UnifiedComposer
              onSubmit={handleLogSubmit}
              disabled={disabled}
              prefilledTransactions={selectedTransactions}
              selectedDate={selectedDate}
            />
          )}

          {activeTab === "review" && (
            <DailyReviewForm
              onSubmitSuccess={() => handleSuccess()}
              disabled={disabled}
              selectedDate={selectedDate}
            />
          )}
        </div>
      </div>

      {/* Date Context Info */}
      <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
        {isToday && <span>📍 Today's entries</span>}
        {isPast && <span>📅 Logging for past date: {new Date(selectedDate).toLocaleDateString()}</span>}
        {isFuture && <span>🔮 Planning ahead: {new Date(selectedDate).toLocaleDateString()}</span>}
      </div>
    </div>
  );
};

export default JournalInput;
